import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { DiagnosticFilters } from '../../types'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  getEcgExamList,
  getCxrayExamList,
  IExamListParams
} from 'services/exam/examService'
import { IExamResponse } from 'types/exam/types'
import { useAppSelector } from 'reduxStore/store'
import { useSuperAdmin } from 'components/rules/PermissionByRole'
import { serializeFilters, deserializeFilters } from '../../utils/filterSerializer'
import { CXRAY_FILTER_SUBTYPES } from '../../constants'
import { DIAGNOSTICS_DETAIL } from 'navigation/CONSTANTS'
import { DiagnosticsListConfig } from '../../types'
import analytics from 'services/analytics'
import { changedFilterNames } from '../../utils/changedFilterNames'

export const useDiagnosticList = (config: DiagnosticsListConfig) => {
  const { examType, storageKey } = config
  const establishmentId = useAppSelector(
    rState => rState.establishment.data?.establishment_id
  )
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  // O tipo de exame é fixo por tela (config.examType), não faz parte dos filtros
  const initialFilters: DiagnosticFilters = useMemo(
    () => ({
      subtypes: CXRAY_FILTER_SUBTYPES,
      diagnosticTypes: [],
      examDateStart: null,
      examDateEnd: null,
      patientId: '',
      examId: '',
      diagnosticId: '',
      status: '0', // Pendente
      review: [],
      priority: []
    }),
    []
  )
  const [diagnostics, setDiagnostics] = useState<IExamResponse[]>([])
  const [filteredDiagnostics, setFilteredDiagnostics] = useState<
    IExamResponse[]
  >([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<DiagnosticFilters>(initialFilters)
  const [appliedFilters, setAppliedFilters] =
    useState<DiagnosticFilters>(initialFilters)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  })
  // Ordenação-padrão da API por tela. NÃO fica no sortModel do grid: 'review_priority'
  // não é uma coluna, e o DataGrid sanitizaria/zeraria o sortModel (caindo em data).
  // O sortModel só carrega ordenação escolhida pelo usuário (colunas reais).
  const defaultOrderBy = examType === 'cxray' ? 'review_priority' : 'acquisition_date'

  // Estado padrão do sortModel por tela. Raio-X abre pela ordenação-padrão da API
  // (relevância clínica) com o sortModel vazio; ECG abre por data (coluna real).
  const defaultSortModel = useMemo<
    Array<{ field: string; sort: 'asc' | 'desc' }>
  >(
    () =>
      examType === 'cxray'
        ? []
        : [{ field: 'acquisition_date', sort: 'desc' }],
    [examType]
  )

  const [sortModel, setSortModel] =
    useState<Array<{ field: string; sort: 'asc' | 'desc' }>>(defaultSortModel)
  const [totalCount, setTotalCount] = useState(0)
  const isSuperAdmin = useSuperAdmin()

  // Ref para versionamento de requisições (latest version only gets processed)
  const requestVersionRef = useRef(0)

  // Helper function to reset pagination
  const resetPagination = useCallback(() => {
    setPaginationModel({ page: 0, pageSize: 25 })
  }, [])

  // Initialize filters on mount from URL or initialFilters
  // This ONLY runs once when component mounts
  useEffect(() => {
    if (searchParams.toString()) {
      const restoredFilters = deserializeFilters(searchParams, initialFilters)
      setFilters(restoredFilters)
      setAppliedFilters(restoredFilters)
    } else {
      setFilters(initialFilters)
      setAppliedFilters(initialFilters)
    }
  }, []) // Empty dependency array: only run on mount

  // Sync filters when URL changes (e.g., browser back/forward or navigation)
  // This runs AFTER initialization, only when searchParams actually changes
  useEffect(() => {
    if (searchParams.toString()) {
      const restoredFilters = deserializeFilters(searchParams, initialFilters)
      setFilters(restoredFilters)
      setAppliedFilters(restoredFilters)
    }
  }, [searchParams])

  /**
   * Busca a lista de exames da API
   * Com proteção contra race condition: apenas a versão mais recente é processada
   */
  const fetchDiagnostics = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    // Incrementar versão e capturar a versão atual desta requisição
    const currentVersion = ++requestVersionRef.current

    try {
      const params = {
        hospital_id: establishmentId || '',
        patient_id: appliedFilters.patientId || undefined,
        id_exam: appliedFilters.examId || undefined,
        result_type:
          appliedFilters.diagnosticTypes.length > 0
            ? `["${appliedFilters.diagnosticTypes.join('","')}"]`
            : undefined,
        subtype:
          examType === 'cxray'
            ? (appliedFilters.subtypes.length > 0
                ? appliedFilters.subtypes
                : CXRAY_FILTER_SUBTYPES
              ).join(',')
            : undefined,
        acquisition_date_start: appliedFilters.examDateStart
          ? appliedFilters.examDateStart.format('YYYY-MM-DD')
          : undefined,
        acquisition_date_end: appliedFilters.examDateEnd
          ? appliedFilters.examDateEnd.format('YYYY-MM-DD')
          : undefined,
        offset: paginationModel.page * paginationModel.pageSize,
        diagnosis_status:
          appliedFilters.status === 'all' ? undefined : appliedFilters.status,
        review:
          examType === 'cxray' && appliedFilters.review.length > 0
            ? appliedFilters.review.join(',')
            : undefined,
        priority:
          examType === 'cxray' && appliedFilters.priority.length > 0
            ? appliedFilters.priority.join(',')
            : undefined,
        // Sem sort do usuário → ordenação-padrão da tela (Raio-X: relevância clínica).
        order_by: sortModel[0]?.field || defaultOrderBy,
        order_direction: sortModel[0]?.sort || 'desc',
        limit: paginationModel.pageSize,
        is_admin: isSuperAdmin
      } as IExamListParams
      const response =
        examType === 'cxray'
          ? await getCxrayExamList(params)
          : await getEcgExamList(params)

      // Verificar se esta é ainda a versão mais recente
      // Se não for, significa que uma requisição mais nova foi feita
      if (currentVersion !== requestVersionRef.current) {
        console.log(
          '[fetchDiagnostics] Ignoring stale response (version',
          currentVersion,
          'current:',
          requestVersionRef.current,
          ')'
        )
        return
      }

      if (response.success && response.data) {
        setDiagnostics(response.data.data)
        setFilteredDiagnostics(response.data.data)
        setTotalCount(response.data.total || 0)
      } else {
        setError('Erro ao buscar análises')
      }
    } catch (err) {
      // Verificar se esta é ainda a versão mais recente antes de aplicar erro
      if (currentVersion === requestVersionRef.current) {
        setError('Erro ao buscar análises')
        console.error('Error fetching diagnostics:', err)
      }
    } finally {
      // Só resetar loading se esta é ainda a versão mais recente
      if (currentVersion === requestVersionRef.current) {
        setIsLoading(false)
      }
    }
  }, [
    establishmentId,
    appliedFilters,
    paginationModel,
    sortModel,
    isSuperAdmin,
    examType,
    defaultOrderBy
  ])

  const handleFilterChange = useCallback(
    (field: keyof DiagnosticFilters, value: any) => {
      setFilters(prev => ({
        ...prev,
        [field]: value
      }))
    },
    []
  )

  const handleSortModelChange = useCallback(
    (newSortModel: Array<{ field: string; sort: 'asc' | 'desc' }>) => {
      setSortModel(newSortModel)
    },
    []
  )

  /** Restaura a ordenação padrão da tela (Raio-X: relevância clínica; ECG: data). */
  const resetSort = useCallback(() => {
    setSortModel(defaultSortModel)
  }, [defaultSortModel])

  /** True quando o usuário ordenou por uma coluna (difere da ordenação padrão). */
  const hasCustomSort = useMemo(() => {
    if (sortModel.length !== defaultSortModel.length) return true
    return sortModel.some(
      (item, index) =>
        item.field !== defaultSortModel[index].field ||
        item.sort !== defaultSortModel[index].sort
    )
  }, [sortModel, defaultSortModel])

  const applyFilters = useCallback(() => {
    // Apply current filters and reset to first page
    setAppliedFilters(filters)
    resetPagination()
    // Update URL with serialized filters
    const serializedParams = serializeFilters(filters)
    setSearchParams(serializedParams)
    // Save filters to sessionStorage for recovery when returning from details
    try {
      sessionStorage.setItem(
        storageKey,
        JSON.stringify({
          ...filters,
          examDateStart: filters.examDateStart?.format('YYYY-MM-DD'),
          examDateEnd: filters.examDateEnd?.format('YYYY-MM-DD')
        })
      )
    } catch (e) {
      console.warn('Failed to save diagnostic filters to sessionStorage:', e)
    }
    if (examType === 'cxray') {
      analytics.track('xray_list_filter_applied', {
        filters: changedFilterNames(filters, initialFilters)
      })
    }
  }, [
    filters,
    resetPagination,
    setSearchParams,
    storageKey,
    examType,
    initialFilters
  ])

  /** Fetch diagnostics when filters or pagination changes */
  useEffect(() => {
    if (establishmentId) {
      fetchDiagnostics()
    }
  }, [
    establishmentId,
    appliedFilters,
    paginationModel,
    sortModel,
    isSuperAdmin
  ])

  /** Polling automático a cada 5 minutos */
  useEffect(() => {
    const interval = setInterval(
      () => {
        if (establishmentId) {
          fetchDiagnostics()
        }
      },
      5 * 60 * 1000
    ) // 5 minutos

    return () => clearInterval(interval)
  }, [establishmentId, fetchDiagnostics])

  const clearFilters = useCallback(() => {
    setFilters(initialFilters)
    setAppliedFilters(initialFilters)
    resetPagination()
    // Clear URL params
    setSearchParams({})
  }, [initialFilters, resetPagination, setSearchParams])

  const handlePaginationChange = useCallback(
    (newPaginationModel: { page: number; pageSize: number }) => {
      // Se o pageSize mudou, resetar para a primeira página
      setPaginationModel(prev => {
        if (prev.pageSize !== newPaginationModel.pageSize) {
          return { page: 0, pageSize: newPaginationModel.pageSize }
        }
        return newPaginationModel
      })
    },
    []
  )

  const formatDate = useCallback((date: string) => {
    return dayjs(date).format('DD/MM/YYYY')
  }, [])

  const handleRowClick = useCallback(
    (row: IExamResponse) => {
      if (examType === 'cxray') {
        analytics.track('xray_exam_opened', { origin: 'row' })
      }
      navigate(DIAGNOSTICS_DETAIL(row.id_exam))
    },
    [navigate, examType]
  )

  return {
    diagnostics,
    filteredDiagnostics,
    filters,
    isLoading,
    error,
    handleFilterChange,
    applyFilters,
    clearFilters,
    formatDate,
    paginationModel,
    handlePaginationChange,
    totalCount,
    fetchDiagnostics,
    sortModel,
    handleSortModelChange,
    resetSort,
    hasCustomSort,
    handleRowClick
  }
}
